import type {
    AnatomyBinding,
    AnatomyPart,
    AnatomyState,
    AnatomyVariant,
    ComponentAnatomySpec,
} from "./componentAnatomy";
import { anatomyTextStyle } from "./componentAnatomyText";

// Mined from designSystem/SideNav: SideNav.module.scss (surface), _row.scss
// (shared row surface, target and ring), SideNavItem, SideNavSubItem and
// SideNavSection .module.scss. The variant axis is the row kind; hover is
// :hover or [data-forced-state="hover"], focus the target's :focus-visible.

const parts: readonly AnatomyPart[] = [
    {
        id: "surface",
        label: "Nav surface",
        element:
            "nav.sideNav: --side-nav-width, --side-nav-bg, right divider; .region insets rows 8px",
    },
    {
        id: "itemBackground",
        label: "Row",
        element:
            ".item (28px), .subItem (24px) or the section .header (24px): row.surface",
    },
    {
        id: "glyph",
        label: "Icon / caret",
        element:
            ".icon 16px slot with recipe-icon-color, or the section .caret (20px box, 12px Triangle)",
    },
    {
        id: "itemLabel",
        label: "Label",
        element: ".label, single line with ellipsis and a truncation tooltip",
    },
    {
        id: "connector",
        label: "Tree line",
        element:
            ".subItems::before under the parent icon; dropped when sub-items carry a status",
    },
    {
        id: "focusRing",
        label: "Focus ring",
        element:
            "Inset box-shadow on the target's stretched ::after, so clipping panels keep it",
    },
];

const states: readonly AnatomyState[] = [
    { id: "resting", label: "Default", selector: "" },
    { id: "hover", label: "Hover", selector: ":hover" },
    { id: "focus", label: "Focus", selector: " :focus-visible::after" },
];

const sharedBindings: readonly AnatomyBinding[] = [
    {
        partId: "surface",
        property: "width",
        via: "--side-nav-width",
        literal: "220px",
    },
    {
        partId: "surface",
        property: "background-color",
        via: "--side-nav-bg",
        token: "--bg-secondary",
    },
    {
        partId: "surface",
        property: "border-right-color",
        token: "--border-divider",
    },
    {
        partId: "surface",
        property: "border-right-width",
        token: "--border-width",
    },
    {
        partId: "surface",
        property: "padding-inline",
        token: "--space-8",
        note: "On each .region, so rows are 204px wide.",
    },
    {
        partId: "itemBackground",
        property: "border-radius",
        token: "--radius-m",
    },
    {
        partId: "itemBackground",
        property: "background-color",
        literal: "transparent",
    },
    {
        partId: "itemBackground",
        property: "transition",
        token: "--duration-fast",
        note: "background-color, ease. Hover sets the duration to 0ms: it appears instantly and fades out.",
    },
    { partId: "itemLabel", property: "color", token: "--fg-secondary" },
];

const hoverBindings: readonly AnatomyBinding[] = [
    {
        partId: "itemBackground",
        property: "background-color",
        token: "--bg-neutral-subtle-hover",
    },
];

const focusBindings: readonly AnatomyBinding[] = [
    {
        partId: "focusRing",
        property: "box-shadow (width)",
        token: "--border-width-thick",
        note: "inset",
    },
    {
        partId: "focusRing",
        property: "box-shadow (color)",
        token: "--focus-ring-color",
    },
];

const itemBindings: readonly AnatomyBinding[] = [
    { partId: "itemBackground", property: "height", literal: "28px" },
    {
        partId: "itemBackground",
        property: "padding-inline-start",
        token: "--space-12",
    },
    {
        partId: "itemBackground",
        property: "padding-inline-end",
        token: "--space-6",
    },
    {
        partId: "itemBackground",
        property: "gap (icon to label)",
        token: "--space-12",
        note: "On the .target link or button.",
    },
    {
        partId: "itemBackground",
        property: "gap (label to caret, trailing)",
        token: "--space-4",
    },
    { partId: "glyph", property: "width", literal: "16px" },
    { partId: "glyph", property: "height", literal: "16px" },
    { partId: "glyph", property: "color", token: "--fg-secondary" },
    ...anatomyTextStyle({
        scale: "s",
        weight: "500",
        mixin: "text-s-14-medium",
        partId: "itemLabel",
    }),
];

const activeBindings: readonly AnatomyBinding[] = [
    {
        partId: "itemBackground",
        property: "background-color",
        token: "--bg-neutral",
        note: "[data-active]; declared after :hover, so hovering keeps it.",
    },
    { partId: "glyph", property: "color", token: "--fg-primary" },
    { partId: "itemLabel", property: "color", token: "--fg-primary" },
];

const subItemBindings: readonly AnatomyBinding[] = [
    { partId: "itemBackground", property: "height", literal: "24px" },
    {
        partId: "itemBackground",
        property: "margin-inline-start",
        token: "--space-32",
        note: "0 with a status, which takes the parent's icon column instead.",
    },
    {
        partId: "itemBackground",
        property: "padding-inline",
        token: "--space-8",
    },
    {
        partId: "itemBackground",
        property: "gap (label to meta)",
        token: "--space-8",
    },
    ...anatomyTextStyle({
        scale: "xs",
        weight: "500",
        mixin: "text-xs-12-medium",
        partId: "itemLabel",
    }),
    { partId: "connector", property: "width", token: "--border-width" },
    {
        partId: "connector",
        property: "background-color",
        token: "--bg-neutral",
    },
    {
        partId: "connector",
        property: "left",
        literal: "calc(var(--space-12) + 16px / 2 - var(--border-width))",
    },
];

const sectionBindings: readonly AnatomyBinding[] = [
    { partId: "itemBackground", property: "height", literal: "24px" },
    {
        partId: "itemBackground",
        property: "padding-inline-start",
        token: "--space-12",
    },
    {
        partId: "itemBackground",
        property: "padding-inline-end",
        token: "--space-6",
    },
    {
        partId: "itemBackground",
        property: "gap (label to caret)",
        token: "--space-4",
    },
    ...anatomyTextStyle({
        scale: "xs",
        weight: "400",
        mixin: "text-xs-12-regular",
        partId: "itemLabel",
    }),
    { partId: "glyph", property: "width", literal: "20px" },
    { partId: "glyph", property: "height", literal: "20px" },
    { partId: "glyph", property: "border-radius", token: "--radius-s" },
    {
        partId: "glyph",
        property: "color",
        token: "--fg-tertiary",
        note: "--fg-primary on --bg-neutral-hover while the caret itself is hovered.",
    },
];

const rowVariant = ({
    hover = hoverBindings,
    ...variant
}: Omit<AnatomyVariant, "stateBindings"> & {
    hover?: readonly AnatomyBinding[];
}): AnatomyVariant => ({
    ...variant,
    stateBindings: { hover, focus: focusBindings },
});

const variants: readonly AnatomyVariant[] = [
    rowVariant({
        id: "item",
        label: "Item",
        selector: " .item",
        restingBindings: itemBindings,
    }),
    rowVariant({
        id: "activeItem",
        label: "Active item",
        selector: " .item[data-active]",
        restingBindings: [...itemBindings, ...activeBindings],
        hover: [],
    }),
    rowVariant({
        id: "subItem",
        label: "Sub-item",
        selector: " .subItem",
        restingBindings: subItemBindings,
    }),
    rowVariant({
        id: "section",
        label: "Section header",
        selector: " .header",
        restingBindings: sectionBindings,
    }),
];

export const sideNavAnatomy: ComponentAnatomySpec = {
    label: "Side nav",
    schematic: "sideNav",
    rootSelector: ".sideNav",
    variantAxisLabel: "Row",
    parts,
    states,
    sharedBindings,
    variants,
};
